import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { queryClient } from '@/queryClient'
import 'markstream-vue/index.css'

import router from '@/router/index'
import components from '@/components/UI'

/* import the fontawesome core */
import { library } from '@fortawesome/fontawesome-svg-core'

/* import font awesome icon component */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

/* import specific icons */
import { faTableList } from '@fortawesome/free-solid-svg-icons'
import { faBookOpen } from '@fortawesome/free-solid-svg-icons'
import { faRotate } from '@fortawesome/free-solid-svg-icons'
import { faSquarePlus } from '@fortawesome/free-solid-svg-icons'
import { faIgloo } from '@fortawesome/free-solid-svg-icons'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { faAnglesRight } from '@fortawesome/free-solid-svg-icons'
import { faAnglesLeft } from '@fortawesome/free-solid-svg-icons'
import { faPencil } from '@fortawesome/free-solid-svg-icons'
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { faTruck } from '@fortawesome/free-solid-svg-icons'
import { faAward } from '@fortawesome/free-solid-svg-icons'
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons'
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons'
import { faHand } from '@fortawesome/free-solid-svg-icons'
import { faTrophy } from '@fortawesome/free-solid-svg-icons'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { faMinus } from '@fortawesome/free-solid-svg-icons'
import { faArrowDown } from '@fortawesome/free-solid-svg-icons'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { faArrowUp } from '@fortawesome/free-solid-svg-icons'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { faStop } from '@fortawesome/free-solid-svg-icons'
import { faPlay } from '@fortawesome/free-solid-svg-icons'
import { faClock } from '@fortawesome/free-solid-svg-icons'
import { faCircle } from '@fortawesome/free-solid-svg-icons'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import { faPlug } from '@fortawesome/free-solid-svg-icons'
import { faSliders } from '@fortawesome/free-solid-svg-icons'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { faCoins } from '@fortawesome/free-solid-svg-icons'
import { faScaleBalanced } from '@fortawesome/free-solid-svg-icons'
import { faArrowDownShortWide } from '@fortawesome/free-solid-svg-icons'
import { faArrowUpShortWide } from '@fortawesome/free-solid-svg-icons'
import { faBell } from '@fortawesome/free-solid-svg-icons'
import { faChartLine } from '@fortawesome/free-solid-svg-icons'
import { faRobot } from '@fortawesome/free-solid-svg-icons'
import { faPaperPlane, faGear, faThumbtack } from '@fortawesome/free-solid-svg-icons'

/* add icons to the library */
library.add(
	faBookOpen,
	faScaleBalanced,
	faArrowUpShortWide,
	faArrowDownShortWide,
	faCoins,
	faTrash,
	faSliders,
	faPlug,
	faClock,
	faPlay,
	faStop,
	faPlus,
	faTableList,
	faRotate,
	faSquarePlus,
	faIgloo,
	faChevronDown,
	faAnglesRight,
	faAnglesLeft,
	faPencil,
	faFloppyDisk,
	faXmark,
	faMagnifyingGlass,
	faTruck,
	faCircleCheck,
	faShieldHalved,
	faAward,
	faHand,
	faTrophy,
	faCheck,
	faMinus,
	faArrowDown,
	faArrowRight,
	faArrowLeft,
	faArrowUp,
	faPenToSquare,
	faCircle,
	faBell,
	faChartLine,
	faRobot,
	faPaperPlane,
	faGear,
	faThumbtack,
)

/* import vue toaster popup message */
import Toaster from '@meforma/vue-toaster'

import store from '@/store/modules'

// Мобильная версия: общий слой стилей для телефонов (подключается после стилей приложения).
import '@/styles/mobile.css'

const app = createApp(App)

const pinia = createPinia()

components.forEach(component => {
	app.component(component.name, component)
})

app
	.use(store)
	.use(pinia)
	.use(VueQueryPlugin, { queryClient })
	.use(router)
	.use(Toaster)
	.component('font-awesome-icon', FontAwesomeIcon)

let _mounted = false
function bootstrap() {
	if (_mounted) return
	_mounted = true
	app.mount('#app')
}

// Handoff имперсонализации: окно открыто как popup (?imp=1) окном админа.
// Ждём postMessage с токеном целевого пользователя, кладём в sessionStorage (per-tab),
// затем монтируем приложение. Токен НЕ передаётся через URL.
const _impFlag = new URLSearchParams(location.search).get('imp')
if (_impFlag === '1' && window.opener) {
	let _impTokenReceived = false
	const _onImpMsg = (event) => {
		if (event.origin !== location.origin) return
		const data = event.data
		if (data && data.type === 'IMP_TOKEN' && data.token) {
			_impTokenReceived = true
			sessionStorage.setItem('user', JSON.stringify({ access_token: data.token, token_type: 'bearer' }))
			sessionStorage.setItem('impersonation', JSON.stringify({ active: true, name: data.name || '' }))
			window.removeEventListener('message', _onImpMsg)
			bootstrap()
		}
	}
	window.addEventListener('message', _onImpMsg)
	const _signalReady = () => {
		try { window.opener && window.opener.postMessage({ type: 'IMP_READY' }, location.origin) } catch { /* ignore */ }
	}
	_signalReady()
	setTimeout(_signalReady, 150)
	setTimeout(_signalReady, 500)
	// Фолбэк: если токен не пришёл за 6с — монтируем как есть (выбросит на логин).
	setTimeout(() => {
		if (!_impTokenReceived) {
			window.removeEventListener('message', _onImpMsg)
			bootstrap()
		}
	}, 6000)
} else {
	bootstrap()
}
